import { useCallback, useEffect, useState } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { useSSEStore } from '../../store/useSSEStore';
import { useExtractionStore } from '../../store/useExtractionStore';
import * as api from '../../api/endpoints';
import type { ApprovalRequest } from '../../types';

function isExtractionGate(p: ApprovalRequest): boolean {
  const skill = p.skill_name || p.skill || '';
  return skill === 'extraction_gate' || p.type === 'result_review' || skill === 'diagram_extraction';
}

export function ExtractionGateBanner() {
  const [gateData, setGateData] = useState<ApprovalRequest | null>(null);
  const [gateStatus, setGateStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const extraction = useProjectStore((s) => s.extraction);
  const setEditMode = useExtractionStore((s) => s.setEditMode);
  const pendingApproval = useSSEStore((s) => s.pendingApproval);

  // Pick up extraction gate from SSE-driven pendingApproval
  useEffect(() => {
    if (pendingApproval && isExtractionGate(pendingApproval) && !gateData) {
      setGateData(pendingApproval);
      setEditMode(true);
    }
  }, [pendingApproval, gateData, setEditMode]);

  // Also poll as fallback
  useEffect(() => {
    const check = async () => {
      const pending = await api.getPendingApprovals();
      if (pending) {
        const gate = pending.find(isExtractionGate);
        if (gate) {
          setGateData(gate);
          setEditMode(true);
        }
      }
    };
    check();
  }, [extraction, setEditMode]);

  const totalComponents = extraction?.zones?.reduce(
    (sum, z) => sum + (z.all_components?.length || 0),
    0,
  ) || 0;

  const handleContinue = useCallback(async () => {
    if (!gateData) return;
    setEditMode(false);
    await api.approve(gateData.request_id);
    useSSEStore.getState().setPendingApproval(null);
    setGateStatus('approved');
  }, [gateData, setEditMode]);

  const handleStop = useCallback(async () => {
    if (!gateData) return;
    setEditMode(false);
    await api.reject(gateData.request_id, 'User stopped after extraction');
    useSSEStore.getState().setPendingApproval(null);
    setGateStatus('rejected');
  }, [gateData, setEditMode]);

  if (!gateData || gateStatus !== 'pending') {
    if (gateStatus === 'approved') {
      return (
        <div className="gate-bar gate-bar-running">
          <span className="gate-bar-dot" />
          Running finalization...
        </div>
      );
    }
    return null;
  }

  return (
    <div className="gate-bar gate-bar-review">
      <span className="gate-bar-badge">Done</span>
      <span className="gate-bar-text">
        {totalComponents} components
        {extraction?.zones && ` across ${extraction.zones.length} zone${extraction.zones.length !== 1 ? 's' : ''}`}
      </span>
      <span className="gate-bar-spacer" />
      <button className="gate-bar-btn gate-bar-btn-primary" onClick={handleContinue}>
        Finalize BoQ
      </button>
      <button className="gate-bar-btn" onClick={handleStop}>
        Stop
      </button>
    </div>
  );
}

export function AutoExtractBanner() {
  const autoApprove = useExtractionStore((s) => s.autoApprovePages);
  const pages = useExtractionStore((s) => s.pages);
  const pageStatuses = useExtractionStore((s) => s.pageStatuses);

  if (!autoApprove || pages.length === 0) return null;

  let doneCount = 0;
  let extractingPage: string | null = null;
  for (const p of pages) {
    const key = `${p.file}:${p.page}`;
    const status = pageStatuses[key];
    if (status === 'done' || status === 'skipped') doneCount++;
    else if (status === 'extracting' && !extractingPage) extractingPage = `p.${p.page}`;
  }

  // Hide when all done or nothing is extracting
  const allDone = doneCount >= pages.length;
  const hasExtracting = Object.values(pageStatuses).some((s) => s === 'extracting');
  if (allDone || !hasExtracting) return null;

  const progress = Math.round((doneCount / pages.length) * 100);

  return (
    <div className="auto-extract-banner">
      <span className="auto-extract-dot" />
      <span className="auto-extract-label">
        Auto-extracting{extractingPage ? ` (${extractingPage})` : ''}
      </span>
      <div className="auto-extract-bar">
        <div className="auto-extract-bar-fill" style={{ width: `${progress}%` }} />
      </div>
      <span className="auto-extract-count">{doneCount}/{pages.length}</span>
    </div>
  );
}
